import React, { useCallback, useMemo, useState, useRef, useEffect } from "react";
import { useSessionDetails } from "../../lib/redux/sessions/data/data-hook";
import { TranscriptBody } from "../SessionDetails/TranscriptView/TranscriptBody";
import { TranscriptAudio } from "../SessionDetails/TranscriptView";
import { SEC_TO_MS } from "../../cfg/const";
import { SessionDetailView } from "../History/views";
import { LoadingState } from "../SessionDetails/Defaults/Loading";
import { NoDataState } from "../SessionDetails/Defaults/NoDataState";
interface SelectedTranscriptProps {
  sessionId: string;
  view: SessionDetailView;
  inView?: boolean;
  onUnselect?: () => void;
}

export function SelectedTranscript({
  sessionId,
  view,
  inView = true,
  onUnselect,
}: SelectedTranscriptProps) {
  const { sessionData, isLoading, refresh } = useSessionDetails({ sessionId });
  
  // Audio playback state
  const [selectedTranscriptIdx, setSelectedTranscriptIdx] = useState<number | undefined>();
  const [currentTimeInMs, setCurrentTimeInMs] = useState(0);
  const currentTimeInMsRef = useRef(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [previousPlayState, setPreviousPlayState] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const totalDuration = useMemo(() => {
    if (!sessionData) return 0;
    return (sessionData?.call_metric?.call_duration ?? 0) * SEC_TO_MS;
  }, [sessionData]);

  // Update transcript selection when time changes
  useEffect(() => {
    if (selectedTranscriptIdx !== undefined && sessionData && sessionData.turns.length > selectedTranscriptIdx) {
      const transcript = sessionData?.turns[selectedTranscriptIdx];
      if (transcript) {
        const timeInMs = transcript.start * SEC_TO_MS;
        setCurrentTimeInMs(timeInMs);
        // Also update the ref so play starts from the correct position
        currentTimeInMsRef.current = timeInMs;
      }
    }
  }, [selectedTranscriptIdx, sessionData]);

  // Reset playback state when session changes
  useEffect(() => {
    currentTimeInMsRef.current = 0;
    setCurrentTimeInMs(0);
    setIsPlaying(false);
    setSelectedTranscriptIdx(undefined);
  }, [sessionId, inView]);

  const handleUpdateTime = (time?: number, isActive?: boolean, prevActive?: boolean) => {
    if (time !== undefined) {
      currentTimeInMsRef.current = time;
      setCurrentTimeInMs(time);
    }
    setIsPlaying(isActive ?? false);
    setPreviousPlayState(prevActive ?? false);
  };

  // Timer effect for updating current time
  useEffect(() => {
    // Clear any existing timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    // Start a new timer if playing
    if (isPlaying) {
      timerRef.current = setInterval(() => {
        const newTime = currentTimeInMsRef.current + (speed * 250);
        if (newTime > totalDuration) {
          setIsPlaying(false);
        }
        const clampedTime = Math.min(newTime, totalDuration);
        currentTimeInMsRef.current = clampedTime;
        setCurrentTimeInMs(clampedTime);
      }, 250);
    }

    // Cleanup function
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isPlaying, speed, totalDuration]); // Re-create timer when isPlaying or speed changes

  const handleForceRefresh = useCallback(() => {
    refresh();
  }, [refresh]);

  if (isLoading) {
    return (
      <div className="flex flex-col bg-white h-fit">
        <div className="flex max-h-[50vh] min-h-[140px] w-full">
          <div className="flex-1 flex items-center justify-center">
            <LoadingState />
          </div>
        </div>
      </div>
    );
  }
  
  if (!sessionData) {
    return (
      <div className="flex flex-col bg-white h-fit">
        <div className="flex max-h-[50vh] min-h-[140px] w-full">
          <div className="flex-1 flex items-center justify-center">
            <NoDataState onRetry={handleForceRefresh} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-white h-fit">
      {/* Main content area with transcript */}
      <div className="flex max-h-[min(45vh,400px)] min-h-[140px] w-full">
        <div className="flex-1 overflow-y-auto">
          <TranscriptBody
            view={view}
            data={sessionData}
            currentTimeInMs={currentTimeInMs}
            isPlaying={isPlaying}
            updateSelectedTranscript={(idx) => {
              setSelectedTranscriptIdx(idx);
              if (isPlaying) {
                setIsPlaying(false);
              }
            }}
            selectedTranscriptIdx={selectedTranscriptIdx}
          />
        </div>
      </div>

      {/* Bottom section with audio controls */}
      <div className="flex-shrink-0">
        <TranscriptAudio
          data={sessionData}
          currentTimeInMs={currentTimeInMs}
          isPlaying={isPlaying}
          updateTime={handleUpdateTime}
          setSpeed={setSpeed}
          speed={speed}
          previousPlayState={previousPlayState}
          totalDuration={totalDuration}
        />
      </div>
    </div>
  );
}
